import { createSlice } from '@reduxjs/toolkit';
import { CellTowerReportListItemType, CellTowerReportResultType } from '../../types/cell-towers/cell-tower-report.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import snackbarUtils from '../../utils/SnackbarUtils';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { getCellTowerReportResults, getCellTowerReportsList, postCellTowerReport } from './cellTowerReports.action';

export interface CellTowerReportsState {
  reportsList: PaginationType<CellTowerReportListItemType> | null;
  reportsListFetchStatus: FETCH_STATUS;
  reportCreateStatus: FETCH_STATUS;
  reportResults: PaginationType<CellTowerReportResultType> | null;
  reportResultsFetchStatus: FETCH_STATUS;
  cellTowerReportsRouteParams: RouteParamsType;
  cellTowerReportResultsRouteParams: RouteParamsType;
}

export const initialCellTowerReportsRouteParams: RouteParamsType = {
  page: 1,
  limit: 10,
  order: '-id',
  q: '',
};

export const initialCellTowerReportResultsRouteParams: RouteParamsType = {
  page: 1,
  limit: 25,
  order: 'rowNumber',
  q: '',
};

const INIT_STATE: CellTowerReportsState = {
  reportsList: null,
  reportsListFetchStatus: FETCH_STATUS.NULL,
  reportCreateStatus: FETCH_STATUS.NULL,
  reportResults: null,
  reportResultsFetchStatus: FETCH_STATUS.NULL,
  cellTowerReportsRouteParams: initialCellTowerReportsRouteParams,
  cellTowerReportResultsRouteParams: initialCellTowerReportResultsRouteParams,
};

const cellTowerReportsSlice = createSlice({
  name: 'cellTowerReports',
  initialState: INIT_STATE,
  reducers: {
    setCellTowerReportsList: (state, action) => {
      state.reportsList = action.payload;
    },
    setCellTowerReportsListFetchStatus: (state, action) => {
      state.reportsListFetchStatus = action.payload;
    },
    setCellTowerReportResults: (state, action) => {
      state.reportResults = action.payload;
    },
    setCellTowerReportResultsFetchStatus: (state, action) => {
      state.reportResultsFetchStatus = action.payload;
    },
    setCellTowerReportCreateStatus: (state, action) => {
      state.reportCreateStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'cellTowerReports') {
        state.cellTowerReportsRouteParams = { ...state.cellTowerReportsRouteParams, ...payload.data };
      }
      if (payload.reducer === 'cellTowerReportsResults') {
        state.cellTowerReportResultsRouteParams = { ...state.cellTowerReportResultsRouteParams, ...payload.data };
      }
    });

    builder
      .addCase(getCellTowerReportsList.pending, state => {
        state.reportsListFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getCellTowerReportsList.rejected, (state, action) => {
        state.reportsListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      })
      .addCase(getCellTowerReportsList.fulfilled, (state, action) => {
        state.reportsListFetchStatus = FETCH_STATUS.FULFILLED;
        state.reportsList = action.payload;
      })
      .addCase(postCellTowerReport.pending, state => {
        state.reportCreateStatus = FETCH_STATUS.PENDING;
      })
      .addCase(postCellTowerReport.rejected, (state, action) => {
        state.reportCreateStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      })
      .addCase(postCellTowerReport.fulfilled, state => {
        state.reportCreateStatus = FETCH_STATUS.FULFILLED;
      })
      .addCase(getCellTowerReportResults.pending, state => {
        state.reportResultsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getCellTowerReportResults.rejected, (state, action) => {
        state.reportResultsFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      })
      .addCase(getCellTowerReportResults.fulfilled, (state, action) => {
        state.reportResultsFetchStatus = FETCH_STATUS.FULFILLED;
        state.reportResults = action.payload;
      });
  },
});

export const {
  setCellTowerReportsList,
  setCellTowerReportsListFetchStatus,
  setCellTowerReportResults,
  setCellTowerReportResultsFetchStatus,
  setCellTowerReportCreateStatus,
} = cellTowerReportsSlice.actions;

export default cellTowerReportsSlice.reducer;
